/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import {
  clearProductFilterEvents,
  removeDataFromPage,
  setSelectedNullCartridge,
  setSortByOrItemsPerPageFocus,
} from '@/actions/shelf/shelf';
import { useFeatureFlag } from '@/features/globalConfig';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useGetCustomPriceFilterLabel } from '@/hooks/useGetCustomPriceFilterLabel';
import { useGetFilterAnalytics } from '@/hooks/useGetFilterAnalytics';
import { useGetSessionFiltersList } from '@/hooks/useGetSessionFiltersList';
import { useInteractionCheck } from '@/hooks/useInteractionCheck';
import type { ReduxState } from '@/types';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import { getContentBlockNames } from '@/utils/analytics/legacyUtils/getContentBlockNames';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { getProductFinderAnalytics } from '@/utils/analytics/legacyUtils/getProductFinderAnalytics';
import { getSelectedSortingOptionLabel, getShelfAnalytics } from '@/utils/analytics/shelfAnalytics';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { getPfmLocationFromIntcmp } from '@/utils/analytics/getPfmLocationFromIntcmp';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import { getPathnameFromAsPath, parseUrl } from '@/utils/urlHelpers';
import { FACETED_URL } from '@/utils/validatorRegex';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProductList } from '../../context/useProductList';
import usePrevious from '@/utils/usePrevious';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { type MappedBrandShelfResults, type MappedBrandShelfResultsAces } from '@/features/brands';
import { eventTypes } from '@/constants/event';
import { getStoredInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import { getContentBlockTypes } from '@/utils/analytics/legacyUtils/getContentBlockTypes';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { sessionStorage } from '@/utils/sessionStorage';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { pageTypes as pageTypesConstants } from '@/constants/page';
import { usePageType } from '@/hooks/usePageType';
import { setupAnalytics } from '@/utils/analytics/config/setupAnalytics';
import root from 'window-or-global';
import { siteSections } from '@/utils/siteSections';
import { useGetShelfPageRecords } from '../../hooks/useGetShelfPageRecords';
import { useYmmeDefaultData } from '@/features/ymme/utils/useYmmeDefaultData';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { useFilteredSortingOptions } from '@/hooks/useFilteredSortingOptions';
import { useLabels } from '@/hooks/useLabels';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { resetAnalyticsDatalayer } from '@/utils/analytics/dataLayer/resetAnalyticsDatalayer';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { type MappedSearchProductData } from '@/features/search';
import {
  type ProductShelfACESResponse,
  type ProductShelfResponse,
  type MerchShelfResponse,
} from '@/features/category';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { shelfPageVariables } from '@/constants/shelfPageVariables';
import { useProductListView } from '../../context/useProductListView';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import {
  type ProductShelfACESResultsModel,
  type ProductShelfResultsModel,
} from '@/api/types/browse-search-types';
import {
  useReviewStatistics,
  useReviewStatisticsForAnalytics,
} from '@/features/customReviews/api/getReviewStatistics';
import { reviewAnalyticsSelector } from '../../utils/reviewAnalyticsSelector';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { getSearchPlatform, isGoogleRetailPlatform } from '@/utils/analytics/getSearchPlatform';
import { getUtagMainVId } from '@/utils/cookie';
import { useSearchServingConfig } from '@/features/search/hooks/useSearchServingConfig';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { useCMSProductFinderAttributes } from '@/features/contentstack/hooks/useCMSProductFinderAttributes';
import { isProductFinder } from '@/features/contentstack/utils/typeGuards/isProductFinder';

const labelMap = {
  featuredSortingOption: 'label.sort.featured',
};

type ShelfAnalyticsParams = {
  shelfCatLabel: string | undefined;
  isBrandShelfDataFetching: boolean;
  isVisualFiltersDisplayedOnShelf: boolean;
  infiniteQueryIsPending: boolean | undefined;
  isFetchingNextPage: boolean;
  brandShelfData: MappedBrandShelfResults | MappedBrandShelfResultsAces;
  searchProductData?: MappedSearchProductData;
  productShelfResultsData:
    | ProductShelfResponse
    | ProductShelfACESResponse
    | MerchShelfResponse
    | undefined;
  recordTypeData?: string[];
};

export const useShelfAnalytics = ({
  shelfCatLabel,
  isBrandShelfDataFetching,
  infiniteQueryIsPending,
  isFetchingNextPage,
  isVisualFiltersDisplayedOnShelf,
  brandShelfData,
  searchProductData,
  productShelfResultsData,
}: ShelfAnalyticsParams) => {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const router = useLegacyRouter();
  const { asPath } = router;
  const { query } = parseUrl(asPath);

  const dispatch = useDispatch();
  const isUtagDefined = useAnalytics();
  const pageViewCall = usePageViewCall();
  const { setPageSpecificData } = usePageSpecificData();

  const selectedFilterValue = useSelector(({ shelf }: ReduxState) => shelf.selectedFilterValue);
  const eventType = useSelector(({ shelf }: ReduxState) => shelf.eventType);
  const selectedNullCartridge = useSelector(({ shelf }: ReduxState) => shelf.selectedNullCartridge);
  const { listViewState } = useProductListView();
  const isList = isBrandShelfPage(router.asPath)
    ? listViewState.isBrandListView
    : listViewState.isListView;

  const dispatchClearProductFilterEvents = () => dispatch(clearProductFilterEvents());
  const dispatchSetSelectedNullCartridge = (value: string) =>
    dispatch(setSelectedNullCartridge(value));
  const dispatchRemoveDataFromPage = () => dispatch(removeDataFromPage());

  const isDealBadgeEnabled = useForteDecisionFlagWithTrueDefault('dealBadgeEnabled');
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const enableVdpDsUniqueDisplay = useForteDecisionFlagWithTrueDefault('enableVdpDsUniqueDisplay');
  const experimentId = useExperimentId();
  const servingConfig = useSearchServingConfig();
  const { acesEnabled } = useAcesFlags();
  const { pageType } = usePageType();
  const isShelfPage =
    pageType === pageTypesConstants.ProductShelf || pageType === pageTypesConstants.shelf;
  const isBonusRewardsBadgeEnabled = useFeatureFlag('BONUS_REWARDS_BADGE_ENABLE') === 'true';

  const { data: headerData } = useHeaderData();
  const preferredVehicle = usePreferredVehicle();

  const { data: storeDetails } = useStoreDetailsData();

  const {
    data: shelfData,
    isSuccess: shelfRequestSuccess,
    isFetching: shelfRequestFetching,
    productSkuIsFetching,
    loadMoreClicked,
    additionalProductSkuDetailsIsFetching,
    sponsoredSkus,
    pagedSkuIds,
  } = useProductList();

  const [analyticsCalled, setAnalyticsCalled] = useState(false);
  const isExpandResultsRef = useRef(false);
  const lastSeenAsPathRef = useRef(asPath);

  const getCustomPriceFilterLabel = useGetCustomPriceFilterLabel();
  const getFilterAnalytics = useGetFilterAnalytics();
  const sessionFiltersList = useGetSessionFiltersList();
  const storeSelected = useIsStoreSelected();

  const userInteracted = useInteractionCheck();

  const dealBadgeEnabled = isDealBadgeEnabled && storeSelected;

  const isUS = locale === countryCodes.us;

  const isBrandPremiumPage = shelfData?.isBrandPremiumPage;
  const shelfBreadcrumb = shelfData?.shelfBreadcrumb;
  const shelfHead = shelfData?.shelfHead;
  const shelfLeftNav = shelfData?.shelfLeftNav;
  const shelfPageBody = shelfData?.shelfPageBody;
  const shelfResultHeader = shelfData?.shelfResultHeader;
  const shelfResultBottom = shelfData?.shelfResultBottom;
  const shelfTippyTop = shelfData?.shelfTippyTop;
  const makeOrModelName = shelfData?.makeorModelName;

  const { data: ymmeData } = useYmmeDefaultData();
  const defaultEngine = ymmeData.defaultEngine;

  const {
    pageSpecificData: {
      pfmLocation,
      searchOriginalPartTypeId,
      searchProductRepositoryId,
      searchResultPosition,
      productFindingMethod,
      ignoreNextPageViewCall,
      eventType: eventTypeAnalytics,
      navigationLink,
      navigationInteraction,
      internalCampaignInteraction,
      finderFeature,
    },
  } = usePageSpecificData();

  const productStrikeThroughPricing: string[] = [];

  const prevLoadMoreClicked = usePrevious(loadMoreClicked);
  const prevPartsLength = usePrevious(shelfData?.shelfParts?.length ?? 0);
  const partsCountBeforeLoadMore = useRef(0);

  if (!prevLoadMoreClicked && loadMoreClicked) {
    partsCountBeforeLoadMore.current = prevPartsLength ?? 0;
  }

  const analyticsParts = useMemo(
    () =>
      !shelfData?.shelfParts
        ? []
        : loadMoreClicked
          ? shelfData.shelfParts.slice(partsCountBeforeLoadMore.current)
          : shelfData.shelfParts,
    [shelfData?.shelfParts, loadMoreClicked],
  );

  const skuView = analyticsParts.map(() => '1');
  const productBestSellerBadge: string[] = analyticsParts.map((part) => {
    return part.bestSeller || part.badges?.includes('BEST_SELLER') ? '1' : '';
  });

  analyticsParts.forEach((part) => {
    const strikethroughProduct = getProductStrikethroughAnalyticValue(
      part.skuPricingAndAvailability,
      part.skuPricingAndAvailability?.discountedPrice,
      strikeThroughPricingEnabled,
    );
    productStrikeThroughPricing.push(strikethroughProduct[0]);
  });

  const pageRecData = useGetShelfPageRecords(shelfPageBody);
  const parsedPath = getPathnameFromAsPath(router.asPath);

  // Helper to get the correct results object (handles ProductShelf, ProductShelfACES, and MerchShelf)
  const getResultsObject = () => {
    if (productShelfResultsData && 'merchShelfResults' in productShelfResultsData) {
      return productShelfResultsData.merchShelfResults;
    }
    if (productShelfResultsData && 'productShelfResults' in productShelfResultsData) {
      return productShelfResultsData.productShelfResults;
    }
    return undefined;
  };
  const resultsObject = getResultsObject();

  const productFinderData = !acesEnabled
    ? (shelfData?.productFinderData ??
      (resultsObject as ProductShelfResultsModel | undefined)?.vehicleProductAttributes)
    : undefined;
  const {
    data: cmsPLPData,
    isPending: isCMSPLPDataPending,
    isCSProductListingPageEnabled,
  } = useContentStackPLPData({ enabled: true });
  const {
    specificationsAttributes,
    totalNumberOfRecords,
    isLoading: isCMSProductFinderLoading,
  } = useCMSProductFinderAttributes();
  const cmsProductFinder = cmsPLPData?.shelfpagebody?.find(isProductFinder);
  const hasCMSYmme = cmsPLPData?.shelfpagebody?.some(
    (block) => 'ymme_content_block' in block && Boolean(block.ymme_content_block),
  );
  const productFitmentFinderAttributes = cmsProductFinder
    ? hasCMSYmme && preferredVehicle && totalNumberOfRecords && specificationsAttributes.length
      ? specificationsAttributes
      : []
    : undefined;
  const isCMSProductFinderReady =
    !isCSProductListingPageEnabled ||
    (!isCMSPLPDataPending && (!cmsProductFinder || !isCMSProductFinderLoading));

  const searchRedirectText =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';

  const totalRecs =
    searchRedirectText && isBrandShelfPage(parsedPath)
      ? (brandShelfData.records?.length ?? 0)
      : (pageRecData?.totalNumRecs ?? 0);

  const partTypeFilterAnalytic =
    brandShelfData.staticNavigation
      ?.find(
        (filter) =>
          filter.dimensionName === 'az_part_group_name_s' ||
          filter.dimensionName === 'az_part_group_name_es_s',
      )
      ?.refinements.map((refinement) => `${refinement.label} (${refinement.count})`)
      .join(', ') ?? '';
  const prevVehicleId = usePrevious(preferredVehicle?.vehicleId);
  const prevAsPath = usePrevious(asPath);
  const prevStoreNumber = usePrevious(storeDetails?.storeNumber);

  const [sortingOptions = []] = useFilteredSortingOptions() || [];
  const showSortOptions = !!sortingOptions.length;
  const selectedsortValue = getSelectedSortingOptionLabel(
    query.sort,
    sortingOptions,
    labels.featuredSortingOption,
  );

  const shelfAnalyticsData = getShelfAnalytics({
    isList,
    parts: analyticsParts,
    shelfBreadcrumb,
    shelfPageBody,
    brandPageBody: brandShelfData,
    shelfCatLabel,
    shelfLeftNav,
    selectedNullCartridge,
    headerData,
    query,
    getCustomPriceFilterLabel,
    getFilterAnalytics,
    sessionFiltersList,
    makeOrModelName,
    defaultEngine,
    dealBadgeEnabled,
    enableVdpDsUniqueDisplay,
    acesEnabled,
    storeSelected,
    showStrikeThroughPricing: strikeThroughPricingEnabled,
    locale,
    isShelfPage,
    isBonusRewardsBadgeEnabled,
  });

  const { data: sponsoredReviewsStatistics, isFetched: reviewStatisticsIsFetched } =
    useReviewStatistics({
      skuNumbers: sponsoredSkus,
      enabled: isUS,
      select: reviewAnalyticsSelector,
    });

  const { combinedData: reviewsStatisticsBatches, isPending: reviewStatisticsBatchesPending } =
    useReviewStatisticsForAnalytics({
      skuNumbers: pagedSkuIds,
      enabled: isUS,
    });

  const reviewsStatisticsData = useMemo(() => {
    const allReviews = {
      productAverageRating: [
        ...(sponsoredReviewsStatistics?.productAverageRating ?? []),
        ...reviewsStatisticsBatches.productAverageRating,
      ],
      productReviewCount: [
        ...(sponsoredReviewsStatistics?.productReviewCount ?? []),
        ...reviewsStatisticsBatches.productReviewCount,
      ],
    };

    if (loadMoreClicked && partsCountBeforeLoadMore.current > 0) {
      return {
        productAverageRating: allReviews.productAverageRating.slice(
          partsCountBeforeLoadMore.current,
        ),
        productReviewCount: allReviews.productReviewCount.slice(partsCountBeforeLoadMore.current),
      };
    }

    return allReviews;
  }, [sponsoredReviewsStatistics, reviewsStatisticsBatches, loadMoreClicked]);

  const googleRetailEnabled = isGoogleRetailPlatform(experimentId);

  const recordType = analyticsParts.map((sku) => {
    return sku.recordType;
  });

  // MARK: unmount

  useEffect(() => {
    return () => {
      setPageSpecificData({
        eventType: '',
      });
      dispatchRemoveDataFromPage();
      resetAnalyticsDatalayer(shelfPageVariables);
      dispatch(setSortByOrItemsPerPageFocus(false, false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MARK: change vehicle, store, or URL

  useEffect(() => {
    const storedInteractionLocation = getStoredInteractionLocation();
    const isAQCompletion = storedInteractionLocation === 'Needs more info';

    if (
      prevVehicleId !== preferredVehicle?.vehicleId ||
      prevAsPath !== asPath ||
      storeDetails?.storeNumber !== prevStoreNumber ||
      isAQCompletion
    ) {
      setAnalyticsCalled(false);
    }
  }, [
    asPath,
    preferredVehicle?.vehicleId,
    prevAsPath,
    prevStoreNumber,
    prevVehicleId,
    storeDetails?.storeNumber,
    headerData,
  ]);

  // MARK: load more

  useEffect(() => {
    if (
      (isBrandShelfDataFetching ||
        shelfRequestFetching ||
        additionalProductSkuDetailsIsFetching ||
        productSkuIsFetching ||
        infiniteQueryIsPending) &&
      analyticsCalled &&
      loadMoreClicked
    ) {
      setAnalyticsCalled(false);
    }
  }, [
    loadMoreClicked,
    analyticsCalled,
    shelfRequestFetching,
    isBrandShelfDataFetching,
    additionalProductSkuDetailsIsFetching,
    infiniteQueryIsPending,
    productSkuIsFetching,
  ]);

  // MARK: analytics call

  useEffect(() => {
    const parsedPath = getPathnameFromAsPath(router.asPath);
    const isBrandsPage = isBrandShelfPage(parsedPath);

    // Transfer the flag from sessionStorage to a ref and clear storage immediately so no
    // unrelated view call can ever read a stale flag. The ref persists across re-fires on
    // the same page; the URL-change guard resets it when a different navigation occurs.
    const flagInStorage = sessionStorage.getItem('expandResultsAnalytics') === 'true';
    if (flagInStorage) {
      isExpandResultsRef.current = true;
      sessionStorage.removeItem('expandResultsAnalytics');
    } else if (router.asPath !== lastSeenAsPathRef.current) {
      isExpandResultsRef.current = false;
    }
    lastSeenAsPathRef.current = router.asPath;
    const isExpandResults = isExpandResultsRef.current;

    // When Expand Results was clicked and analytics has already fired for the current URL,
    // force-reset so the analytics re-fires for the new expanded results page.
    if (isExpandResults && analyticsCalled) {
      setAnalyticsCalled(false);
      return;
    }

    const isAnalyticsReady = isUtagDefined && !ignoreNextPageViewCall && !analyticsCalled;

    const isReviewsDataReady =
      (reviewStatisticsIsFetched && !reviewStatisticsBatchesPending) || !isUS;

    const isDataReady = shelfRequestSuccess && isReviewsDataReady;

    const isNotFetching =
      !productSkuIsFetching && !shelfRequestFetching && !isBrandShelfDataFetching;

    const isUserInteractionReady =
      userInteracted &&
      (loadMoreClicked
        ? !additionalProductSkuDetailsIsFetching && !infiniteQueryIsPending && !isFetchingNextPage
        : true);

    // Universal fitment products (shown after Expand Results) may have fulfillmentOptions = null,
    // which would permanently block analytics. When the flag is set, treat pricing-loaded state
    // (skuPricingAndAvailability != null) as sufficient — fulfillmentOptions may be null for
    // universal products.
    const isFulfillmentDataReady =
      !analyticsParts.length ||
      (analyticsParts.length > 0 &&
        analyticsParts.every((part) =>
          isExpandResults
            ? part.skuPricingAndAvailability != null
            : part.skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions != null &&
              Array.isArray(part.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions),
        ));

    if (
      isAnalyticsReady &&
      isDataReady &&
      isNotFetching &&
      isUserInteractionReady &&
      isFulfillmentDataReady &&
      isCMSProductFinderReady
    ) {
      const pageName = isBrandsPage
        ? typeof router.query.brandName === 'string'
          ? router.query.brandName
          : router.query.brandName?.join()
        : analyticsDynamicPageName(shelfBreadcrumb, 'shelf');

      const productFinderAnalytics = getProductFinderAnalytics(
        productFinderData,
        productFitmentFinderAttributes,
      );
      const segmentArrayCount = asPath.split('/').length;
      const hasTrailingSlash = asPath.endsWith('/');
      const segmentCount = segmentArrayCount - 1 - (hasTrailingSlash ? 1 : 0);
      let analyticsPageType:
        | 'catalog_shelf'
        | 'faceted_nav'
        | 'make_part_type'
        | 'Brand Profile'
        | 'Brand Profile Premium'
        | 'model_part_type'
        | 'year_make_model_part_type' = pageTypes.productShelf;
      const isFacetedUrl = FACETED_URL.test(parsedPath);
      if (isFacetedUrl) {
        analyticsPageType = pageTypes.facetedNav;
      } else if (isBrandsPage) {
        analyticsPageType = isBrandPremiumPage ? pageTypes.brandShelfPremium : pageTypes.brandShelf;
      } else {
        switch (segmentCount) {
          case 3:
            analyticsPageType = pageTypes.makePartType;
            break;

          case 4:
            analyticsPageType = pageTypes.modelPartType;
            break;

          case 5:
            analyticsPageType = pageTypes.yearMakeModelPartType;
            break;

          default:
            break;
        }
      }
      const hasIntcmp = Array.isArray(router.query.intcmp)
        ? router.query.intcmp.some(Boolean)
        : Boolean(router.query.intcmp);
      const intcmpValue = Array.isArray(router.query.intcmp)
        ? (router.query.intcmp.filter(Boolean)[0] ?? '')
        : (router.query.intcmp ?? '');

      // Check if intcmp indicates PDP Comparison Chart (format: PDP:CTA:...)
      const intcmpValues = Array.isArray(router.query.intcmp)
        ? router.query.intcmp
        : [router.query.intcmp];
      const isFromPDPComparison = intcmpValues.some(
        (value) => typeof value === 'string' && value.startsWith('PDP:CTA'),
      );

      const previousPageURL = root.utag_data?.previousPageURL;
      const documentReferrer = root.document.referrer;
      const isFromFixFinder =
        (typeof previousPageURL === 'string' && previousPageURL.includes('/lp/fix-finder')) ||
        (typeof documentReferrer === 'string' && documentReferrer.includes('/lp/fix-finder'));

      // Check if user came from a Finder feature (takes precedence over internal campaigns)
      const isFinderFeature =
        isFromFixFinder ||
        isFromPDPComparison ||
        Boolean(finderFeature) ||
        productFindingMethod === 'Finder';

      const pfmLocationFromIntcmp = getPfmLocationFromIntcmp({
        intcmp: router.query.intcmp,
        previousPageURL,
        categoryPageType: sessionStorage.getItem('categorypagetype'),
      });
      const pfmLocationAnalytics = pfmLocationFromIntcmp ?? pfmLocation;
      const isInternalCampaign = hasIntcmp && Boolean(pfmLocationFromIntcmp) && !isFinderFeature;

      if (root.location.href !== root.utag_data?.pageURL) {
        //if coming in from category page, useAnalytics gets called before the url changes since shelf and category share the same general route
        //we need to re-setup analytics so it has the correct pageURL in this scenario
        setupAnalytics(false, router.asPath, isUtagDefined);
      }

      const removeUnusedAnalyticsData = ['rewardPoints'];
      let pageType: 'catalog_shelf' | 'Brand Profile Premium' | 'Brand Profile' =
        pageTypes.productShelf;

      if (isBrandsPage) {
        pageType = isBrandPremiumPage ? pageTypes.brandShelfPremium : pageTypes.brandShelf;
      }

      const isUnitOfMeasureMatch = !acesEnabled
        ? ((resultsObject as ProductShelfResultsModel | undefined)?.isUnitOfMeasureMatch ?? false)
        : ((resultsObject as ProductShelfACESResultsModel | undefined)?.unitOfMeasureMatch ??
          false);

      const hasFromStringSearchParam = query.fromString && query.fromString === 'search';
      const productFindingMethodFromUtag =
        typeof root.utag_data?.productFindingMethod === 'string'
          ? root.utag_data.productFindingMethod
          : '';
      const productFindingMethodFromSession = sessionStorage.getItem('productFindingMethod') ?? '';
      const reviewsData = isUS ? reviewsStatisticsData : {};
      const selectedPlatform = sessionStorage.getItem('pbs-search-engine');
      const stageName = sessionStorage.getItem('search-stage-name');
      const responseTimeMs = sessionStorage.getItem('search-query-time');

      // Google Cloud Retail analytics fields (all from API)
      const searchPlatform = googleRetailEnabled ? getSearchPlatform(experimentId) : undefined;
      const visitorId = googleRetailEnabled ? getUtagMainVId() : undefined;
      const userId = headerData?.myAccountMap?.customerID;
      const attributionToken = googleRetailEnabled
        ? (productShelfResultsData?.attributionToken ?? brandShelfData.attributionToken)
        : undefined;
      const filter = googleRetailEnabled
        ? (productShelfResultsData?.filter ?? brandShelfData.filter)
        : undefined;

      // Persist attributionToken for Order Confirmation page
      if (googleRetailEnabled && attributionToken) {
        sessionStorage.setItem('googleRetailAttributionToken', attributionToken);
      }

      const resolvedProductFindingMethod = (() => {
        if (isFinderFeature) {
          return 'Finder';
        }
        if (isInternalCampaign) {
          return 'Internal Campaign';
        }
        if (searchRedirectText) {
          return 'Internal Search';
        }
        return (
          productFindingMethodFromSession || productFindingMethod || productFindingMethodFromUtag
        );
      })();

      const utagData = {
        pageName: getPageName(pageType, pageName ?? ''),
        siteSection: siteSections.siteSectionProductShelf,
        pageType: isBrandsPage ? pageTypes.brandShelf : pageTypes.productShelf,
        pageHierarchy: `Home|${
          pageName?.includes('-')
            ? pageName
                .replace(/-/g, ' ')
                .replace(/:/g, '|')
                .replace(/\b([a-z])/g, (x) => x.toUpperCase())
            : (pageName?.replace(/\b([a-z])/g, (x) => x.toUpperCase()) ?? '')
        }`,
        searchType:
          searchProductData?.isUnitOfMeasureMatch || isUnitOfMeasureMatch
            ? 'UnitOfMeasure'
            : undefined,
        contentBlockNames: [
          ...getContentBlockNames(shelfTippyTop),
          ...getContentBlockNames(shelfResultHeader),
          ...getContentBlockNames(shelfResultBottom),
          ...getContentBlockNames(shelfPageBody),
          ...getContentBlockNames(shelfLeftNav),
          ...getContentBlockNames(shelfHead),
        ],
        contentBlockTypes: [
          ...getContentBlockTypes(shelfTippyTop),
          ...getContentBlockTypes(shelfResultHeader),
          ...getContentBlockTypes(shelfResultBottom),
          ...getContentBlockTypes(shelfPageBody),
          ...getContentBlockTypes(shelfLeftNav),
          ...getContentBlockTypes(shelfHead),
        ],
        seoPageType: getSeoPageType(analyticsPageType, router.asPath),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        selectedsortValue: showSortOptions ? selectedsortValue : null,
        ...productFinderAnalytics,
        searchOriginalPartTypeId,
        searchProductRepositoryId,
        searchResultPosition,
        ...shelfAnalyticsData,
        eventType:
          eventType ||
          (isExpandResults
            ? 'expandResults'
            : loadMoreClicked
              ? 'loadMore'
              : searchRedirectText && !!totalRecs
                ? 'successfulSearch'
                : searchRedirectText && totalRecs === 0
                  ? 'nullSearch'
                  : undefined),
        selectedFilterValue: selectedFilterValue,
        ...(searchRedirectText
          ? {
              searchKeyword: searchRedirectText,
            }
          : hasFromStringSearchParam && !!query.filterByKeyWord
            ? {
                searchKeyword: query.filterByKeyWord,
              }
            : {}),
        searchMethod: sessionStorage.getItem('searchMethod') || '',
        prodView: eventTypes.prodView,
        skuView: skuView,
        productFindingMethod: resolvedProductFindingMethod,
        ...(isFinderFeature &&
          (finderFeature
            ? { finderFeature: String(finderFeature) }
            : isFromPDPComparison
              ? { finderFeature: 'PDP Comparison Chart' }
              : isFromFixFinder
                ? { finderFeature: 'Fix Finder' }
                : {})),
        internalCampaignInteraction: isInternalCampaign
          ? internalCampaignInteraction || 'internalCampaignInteraction'
          : internalCampaignInteraction,
        ...(hasIntcmp && { campaignID: intcmpValue }),
        productFitmentBanner:
          eventTypeAnalytics === eventTypes.productFitmentBanner ? eventTypeAnalytics : '',
        productToastNotification:
          eventTypeAnalytics === eventTypes.productToastNotification ? eventTypeAnalytics : '',
        pfmLocation: pfmLocationAnalytics,
        navigationLink,
        navigationInteraction,
        productStrikeThroughPricing: productStrikeThroughPricing,
        pageLazyLoad: false,
        partTypeFiltersAvailable: partTypeFilterAnalytic,
        productBestSellerBadge: productBestSellerBadge,
        ...reviewsData,
        ...(isVisualFiltersDisplayedOnShelf && {
          productFilterSelection: 'Visual Filter: Composition: Synthetic',
          productFindingMethod: 'Visual Part Type Search',
        }),
        ...(userId && { GCMID: userId }),
        // Google Cloud Retail fields (only when using Google Retail platform)
        ...(googleRetailEnabled && {
          filter,
          attributionToken,
          visitorId,
          searchPlatform,
          servingConfig,
        }),
        recordType,
        ...(selectedPlatform && { selected_platform: selectedPlatform }),
        ...(stageName && { stage_name: stageName }),
        ...(responseTimeMs && { response_time_ms: Number(responseTimeMs) }),
      };

      pageViewCall(utagData, [
        ...removeUnusedAnalyticsData,
        ...getAnalyticsRelativeComplementOf(utagData, undefined),
      ]);
      dispatchClearProductFilterEvents();
      setAnalyticsCalled(true);
      sessionStorage.removeItem('searchMethod');
      sessionStorage.removeItem('productFindingMethod');
      sessionStorage.setItem('performedSearch', 'false');
      //clear search variables after pageViewCall
      setPageSpecificData({
        searchOriginalPartTypeId: '',
        searchProductRepositoryId: '',
        searchResultPosition: '',
        productFindingMethod: '',
        pfmLocation: '',
        navigationLink: '',
        navigationInteraction: '',
        internalCampaignInteraction: '',
        finderFeature: '',
      });
      sessionStorage.setItem('popularSearchClicked', 'false');
      isExpandResultsRef.current = false;
      if (selectedNullCartridge) {
        dispatchSetSelectedNullCartridge('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUtagDefined,
    shelfRequestSuccess,
    shelfRequestFetching,
    isBrandShelfDataFetching,
    productSkuIsFetching,
    asPath,
    shelfData,
    shelfData?.shelfParts,
    preferredVehicle?.catalogVehicleId,
    userInteracted,
    analyticsCalled,
    analyticsParts,
    additionalProductSkuDetailsIsFetching,
    infiniteQueryIsPending,
    isFetchingNextPage,
    loadMoreClicked,
    reviewStatisticsIsFetched,
    reviewStatisticsBatchesPending,
    reviewsStatisticsData,
    cmsProductFinder,
    hasCMSYmme,
    productFitmentFinderAttributes,
    isCMSProductFinderLoading,
    isCMSProductFinderReady,
    isCMSPLPDataPending,
    isCSProductListingPageEnabled,
    locale,
    strikeThroughPricingEnabled,
  ]);
};
